import { useEffect, useState } from 'react'

import { AnimatePresence, motion } from 'framer-motion'

import { track } from '@onl-web/shared/analytics'
import { useExitWebView, useFunnel } from '@onl-web/shared/hooks'

import couponBronze from '@/assets/icons/coupon-bronze.png'
import couponGold from '@/assets/icons/coupon-gold.png'
import couponSilver from '@/assets/icons/coupon-silver.png'
import AlertModal from '@/components/AlertModal'
import CompletionToast from '@/components/CompletionToast'
import type { CompletionToastVariant } from '@/components/CompletionToast/utils'
import EnergyToast from '@/components/EnergyToast'
import Spinner from '@/components/Spinner'
import WinnerToast from '@/components/WinnerToast'
import {
  APP_REDIRECT_URL,
  DRAW_ANIMATION_MIN_DURATION_MS,
  GACHA_EVENT_ID,
  NETWORK_ERROR_DESCRIPTION,
} from '@/constants'
import { devLog, useDevQueryLog } from '@/hooks/useDevLog'
import { useGetCoinBalance } from '@/query/coins/useGetCoinBalance'
import { usePostGachaDraw } from '@/query/gacha/usePostGachaDraw'
import type { GachaDrawResponse } from '@/types/gacha'

import GachaLayout from './layout'
import CouponStep from './steps/CouponStep'
import DrawingOverlay from './steps/DrawingOverlay'
import QuizStep from './steps/QuizStep'
import ResultStep from './steps/ResultStep'
import { CouponStepWrapper, FULL_SIZE_STYLE, LoadingWrapper } from './styles'

// 쿠폰 이미지 프리로드 — Lottie가 external fetch하므로 미리 브라우저 캐시에 적재
;[couponGold, couponSilver, couponBronze].forEach((src) => {
  const img = new Image()
  img.src = src
})

const LOADING_TIMEOUT_MS = 7_000

type GachaStep = 'coupon' | 'drawing' | 'result' | 'quiz'

interface GachaData {
  drawResult?: GachaDrawResponse
}

export default function GachaPage() {
  const { step, data, setStep, reset } = useFunnel<GachaStep, GachaData>('coupon')
  const [alertModal, setAlertModal] = useState<{
    title: string
    description?: string
    onConfirm?: () => void
  } | null>(null)
  const [energyToastMessage, setEnergyToastMessage] = useState<string | null>(null)
  const [completionVariant, setCompletionVariant] = useState<CompletionToastVariant | null>(null)
  const exitWebView = useExitWebView(APP_REDIRECT_URL)

  const coinQuery = useGetCoinBalance()
  const gachaEventId = Number(GACHA_EVENT_ID)
  const drawMutation = usePostGachaDraw(gachaEventId)

  const coinBalance = coinQuery.data?.balance ?? 0

  useDevQueryLog('coins/balance', coinQuery)

  useEffect(() => {
    if (!coinQuery.error) return
    setAlertModal({
      title: '네트워크 연결을 확인해주세요',
      description: NETWORK_ERROR_DESCRIPTION,
      onConfirm: exitWebView,
    })
  }, [coinQuery.error, exitWebView])

  useEffect(() => {
    if (!coinQuery.isLoading) return
    const timer = setTimeout(() => {
      setAlertModal({
        title: '네트워크 연결을 확인해주세요',
        description: NETWORK_ERROR_DESCRIPTION,
        onConfirm: exitWebView,
      })
    }, LOADING_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [coinQuery.isLoading, exitWebView])

  useEffect(() => {
    if (!drawMutation.isPending) return
    const timer = setTimeout(() => {
      drawMutation.reset()
      setAlertModal({
        title: '네트워크 연결을 확인해주세요',
        description: NETWORK_ERROR_DESCRIPTION,
      })
    }, LOADING_TIMEOUT_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawMutation.isPending])

  useEffect(() => {
    if (step !== 'drawing') return

    const timer = setTimeout(() => {
      setStep('result')
    }, DRAW_ANIMATION_MIN_DURATION_MS)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  const handleDraw = () => {
    track('click_gacha_btn', {})

    if (drawMutation.isPending) return

    drawMutation.mutate(undefined, {
      onSuccess: (result) => {
        devLog(
          `draw 성공 — tier=${result.rewardTier}, reward=${result.rewardName}, isPityEligible=${result.isPityEligible}, pity=${result.pityGauge.current}/${result.pityGauge.threshold}, 잔액=${result.remainingCoinBalance}`
        )
        setStep('drawing', { drawResult: result })
      },
      onError: (err) => {
        devLog(`draw 실패 — ${err instanceof Error ? err.message : String(err)}`)
        setAlertModal({ title: '쿠폰을 찾을 수 없어요', description: NETWORK_ERROR_DESCRIPTION })
      },
    })
  }

  const renderStep = () => {
    if (step === 'result') {
      return (
        <motion.div
          key='result'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          style={{ width: '100%', minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
          {data.drawResult && (
            <ResultStep
              drawResult={data.drawResult}
              onRetry={reset}
              onError={(title, description) => setAlertModal({ title, description })}
            />
          )}
        </motion.div>
      )
    }

    return (
      <motion.div key='main' exit={{ opacity: 0 }} transition={{ duration: 0.15 }} style={FULL_SIZE_STYLE}>
        <CouponStepWrapper isDrawing={step === 'drawing'}>
          <CouponStep
            onDraw={handleDraw}
            onQuiz={() => setStep('quiz')}
            coinBalance={coinBalance}
            isCoinLoading={false}
            isDrawing={drawMutation.isPending}
            onMissionComplete={setCompletionVariant}
          />
        </CouponStepWrapper>
        {step === 'drawing' && <DrawingOverlay />}
      </motion.div>
    )
  }

  if (coinQuery.isLoading) {
    return (
      <GachaLayout>
        <LoadingWrapper>
          <Spinner size='medium' />
        </LoadingWrapper>
        <AlertModal
          isOpen={alertModal !== null}
          title={alertModal?.title ?? ''}
          description={alertModal?.description}
          onConfirm={() => {
            const action = alertModal?.onConfirm
            setAlertModal(null)
            action?.()
          }}
        />
      </GachaLayout>
    )
  }

  return (
    <GachaLayout>
      {step === 'quiz' ? (
        <div style={{ width: '100%', minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
          <QuizStep
            onBack={(showEnergyToast) => {
              setStep('coupon')
              if (showEnergyToast) {
                setEnergyToastMessage('퀴즈 풀고 에너지 1개를 받았어요')
              }
            }}
            onError={(title, description) => setAlertModal({ title, description })}
          />
        </div>
      ) : (
        <AnimatePresence mode='wait'>{renderStep()}</AnimatePresence>
      )}
      <AlertModal
        isOpen={alertModal !== null}
        title={alertModal?.title ?? ''}
        description={alertModal?.description}
        onConfirm={() => {
          const action = alertModal?.onConfirm
          setAlertModal(null)
          action?.()
        }}
      />
      <EnergyToast message={energyToastMessage} onDismiss={() => setEnergyToastMessage(null)} />
      <CompletionToast variant={completionVariant} onDismiss={() => setCompletionVariant(null)} />
      <WinnerToast isActive={step === 'coupon'} />
    </GachaLayout>
  )
}
